import { Box, Chip } from '@mui/material';
import { Children, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';

const TAG_STYLES: Record<string, { bg: string; fg: string }> = {
  security: { bg: '#fee2e2', fg: '#b91c1c' },
  added: { bg: '#dcfce7', fg: '#15803d' },
  fixed: { bg: '#dbeafe', fg: '#1d4ed8' },
  changed: { bg: '#fef3c7', fg: '#b45309' },
  removed: { bg: '#e5e7eb', fg: '#374151' },
  deprecated: { bg: '#e5e7eb', fg: '#374151' },
};

function getTextContent(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  return '';
}

const markdownComponents = {
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...props} target='_blank' rel='noreferrer' />
  ),
  strong: ({ children }: { children?: ReactNode }) => {
    const text = getTextContent(Children.toArray(children)).trim();
    const style = TAG_STYLES[text.toLowerCase()];
    if (!style) return <strong>{children}</strong>;
    return (
      <Chip
        label={text}
        size='small'
        sx={{
          mr: 1,
          bgcolor: style.bg,
          color: style.fg,
          fontWeight: 600,
          height: 22,
        }}
      />
    );
  },
};

/**
 * Renders a release note's markdown body with the shared tag-chip styling
 * (Security / Added / Fixed / …). Reused by the Release Notes page and the
 * "What's New" dialog so both stay visually identical.
 */
export default function ReleaseNoteContent({ content }: { content: string }) {
  return (
    <Box
      sx={{
        '& a': { color: 'primary.main' },
        '& h1, & h2, & h3': { mt: 2, mb: 1 },
        '& ul, & ol': { pl: 3 },
        '& p': { my: 1 },
        '& img': {
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
          my: 2,
          borderRadius: 1,
        },
      }}
    >
      <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
    </Box>
  );
}
